import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, Flex, Spinner, Stack, Text, VStack,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
import WithSubnavigation from "./navbar";

import { API_BASE } from "../../config";
import { performLogout } from "../../utils/logout";

const hasCookie = (name) => {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .some((entry) => entry.startsWith(`${name}=`));
};

function PharmacistHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies, , removeCookie] = useCookies(["token"]);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);

  console.log("PharmacistHome mounted, path:", location.pathname, "token:", !!cookies.token);

  const Logout = () => performLogout(removeCookie);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/pharmacist/me`, {
          withCredentials: true,
        });
        if (data?.Username) {
          setUsername(data.Username);
          setName(data.Name || data.Username);
          return;
        }
      } catch (error) {
        console.log("pharmacist/me failed, falling back to myInfo", error.message);
      }

      try {
        const verifyRes = await axios.post(API_BASE, {}, { withCredentials: true });
        if (!verifyRes.data?.status) return;
        const uname = verifyRes.data.user;
        setUsername(uname);
        const infoRes = await axios.get(`${API_BASE}/pharmacist/myInfo/${uname}`, {
          withCredentials: true,
        });
        setName(infoRes.data?.Name || uname);
      } catch (error) {
        console.error("Error fetching pharmacist profile:", error);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      try {
        const [pendingRes, completedRes, rejectedRes] = await Promise.all([
          axios.get(`${API_BASE}/order/pharmacist/list`, {
            params: { status: "pending" },
            withCredentials: true,
          }),
          axios.get(`${API_BASE}/order/pharmacist/list`, {
            params: { status: "delivered" },
            withCredentials: true,
          }),
          axios.get(`${API_BASE}/order/pharmacist/list`, {
            params: { status: "cancelled" },
            withCredentials: true,
          }),
        ]);
        setPendingCount(pendingRes.data?.orders?.length || pendingRes.data?.total || 0);
        setCompletedCount(completedRes.data?.orders?.length || completedRes.data?.total || 0);
        setRejectedCount(rejectedRes.data?.orders?.length || rejectedRes.data?.total || 0);
      } catch (error) {
        console.error("Error fetching counts:", error);
        setPendingCount(0);
        setCompletedCount(0);
        setRejectedCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchCounts();
  }, []);

  return (
    <>
      <div style={{ position: "fixed", top: "0", left: "0", right: "0", zIndex: "1000" }}>
        <WithSubnavigation
          username={username}
          name={name}
          isLoggedIn={!!cookies.token}
          openChangePasswordModal={() => {}}
          onLogout={Logout}
          notifications={[]}
        />
      </div>

      <Box
        bg="#f5f7fb"
        minH="100vh"
        pt="96px"
        pb="32px"
        px={{ base: 4, md: 8 }}
      >
        <VStack spacing={8} align="stretch">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="#333">
              Orders Dashboard
            </Text>
            <Text fontSize="md" color="#666" mt={2}>
              Welcome back! Here's a quick overview of your orders.
            </Text>
          </Box>

          {loading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner size="lg" color="#FF6B35" />
            </Flex>
          ) : (
            <Flex direction={{ base: "column", md: "row" }} gap={6} wrap="wrap">
              {/* Pending Orders Card */}
              <Box
                bg="white"
                borderRadius="20px"
                p={8}
                shadow="md"
                flex={{ base: "1", md: "1" }}
                minW="280px"
                borderLeft="8px solid #FF9800"
              >
                <VStack spacing={4} align="flex-start">
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="#FF9800" textTransform="uppercase">
                      Pending Orders
                    </Text>
                    <Text fontSize="4xl" fontWeight="bold" color="#333">
                      {pendingCount}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="#666">
                    Orders awaiting fulfillment and dispatch
                  </Text>
                  <Button
                    mt="auto"
                    colorScheme="orange"
                    size="md"
                    onClick={() => {
                      console.log("Navigating to pending orders");
                      navigate("/pharmacist/orders?status=pending");
                    }}
                  >
                    View Pending Orders
                  </Button>
                </VStack>
              </Box>

              {/* Completed Orders Card */}
              <Box
                bg="white"
                borderRadius="20px"
                p={8}
                shadow="md"
                flex={{ base: "1", md: "1" }}
                minW="280px"
                borderLeft="8px solid #4CAF50"
              >
                <VStack spacing={4} align="flex-start">
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="#4CAF50" textTransform="uppercase">
                      Completed Orders
                    </Text>
                    <Text fontSize="4xl" fontWeight="bold" color="#333">
                      {completedCount}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="#666">
                    Orders successfully delivered to customers
                  </Text>
                  <Button
                    mt="auto"
                    colorScheme="green"
                    size="md"
                    onClick={() => navigate("/pharmacist/orders?status=delivered")}
                  >
                    View Completed Orders
                  </Button>
                </VStack>
              </Box>

              {/* Rejected/Cancelled Orders Card */}
              <Box
                bg="white"
                borderRadius="20px"
                p={8}
                shadow="md"
                flex={{ base: "1", md: "1" }}
                minW="280px"
                borderLeft="8px solid #E53E3E"
              >
                <VStack spacing={4} align="flex-start">
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="#E53E3E" textTransform="uppercase">
                      Rejected Orders
                    </Text>
                    <Text fontSize="4xl" fontWeight="bold" color="#333">
                      {rejectedCount}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="#666">
                    Orders cancelled or rejected
                  </Text>
                  <Button
                    mt="auto"
                    colorScheme="red"
                    size="md"
                    onClick={() => navigate("/pharmacist/orders?status=cancelled")}
                  >
                    View Rejected Orders
                  </Button>
                </VStack>
              </Box>
            </Flex>
          )}

          {/* Quick Actions or Additional Info */}
          <Box bg="white" borderRadius="20px" p={8} shadow="md" mt={4}>
            <Text fontSize="lg" fontWeight="600" color="#333" mb={4}>
              Quick Actions
            </Text>
            <Flex gap={4} direction={{ base: "column", md: "row" }} flex="wrap">
              <Button
                variant="outline"
                colorScheme="blue"
                onClick={() => navigate("/pharmacist/orders")}
              >
                View All Orders
              </Button>
              <Button variant="outline" colorScheme="gray">
                Generate Report
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Box>
    </>
  );
}

export default PharmacistHome;
